import { transition } from '@helpers/animate';
import brand, { colourWithTransparency } from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { SystemIcon } from '@helpers/icons';
import { Device, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import styled, { css } from 'styled-components';
const hoverStyles = css `
  color: var(--fgHoverColour);
  background-color: var(--bgHoverColour);
  background-size: 100% 100%;
  border-color: var(--borderHoverColour);

  ${() => IconWrapper} {
    color: var(--fgHoverColour);
    background-color: transparent;
  }

  &[data-icon-only='false'][data-icon-type=${SystemIcon.ArrowRight}] {
    ${() => IconWrapper} {
      transform: translateX(4px);
    }
  }

  &[data-icon-only='false'][data-icon-type=${SystemIcon.ArrowDown}] {
    ${() => IconWrapper} {
      transform: rotate(90deg) translateX(2px);
    }
  }
`;
const activeStyles = css `
  color: var(--fgActiveColour);
  background-color: var(--bgActiveColour);
  background-size: 100% 100%;
  border-color: var(--borderActiveColour);

  ${() => IconWrapper} {
    color: var(--fgActiveColour);
    background-color: transparent;
  }
`;
export const DefaultButtonStyles = css `
  ${ButtonReset};
  ${transition('background-color, border-color, color', 0.2)};

  --buttonHeight: ${fluid(48, 60)};

  --bgColour: ${brand.primary.teal.teal75};
  --bgHoverColour: ${brand.primary.teal.teal50};
  --bgActiveColour: ${brand.primary.teal.teal75};
  --borderColour: ${brand.primary.teal.teal75};
  --borderHoverColour: ${brand.primary.teal.teal50};
  --borderActiveColour: ${brand.primary.teal.teal100};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.black};

  display: inline-flex;
  gap: 12px;
  align-items: center;
  border: 2px solid var(--borderColour);
  background-color: var(--bgColour);
  color: var(--fgColour);
  min-height: var(--buttonHeight);
  padding: 4px ${fluid(24, 32)};
  text-decoration: none;
  user-select: none;
  width: auto;
  border-radius: 30px;

  &[data-fill='right'] {
    background-position: left center;
  }

  &[data-fill='left'] {
    background-position: right center;
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('background-size, border-color, color, transform', 0.15)};

    &:active {
      transform: translateY(1.5px);
    }
  }

  span& {
    :focus-visible & {
      outline: auto;
      outline-offset: 5px;
      outline-width: 2px;
      outline-color: var(--borderHoverColour, var(--bgColour));
    }

    button:focus-visible &,
    a:focus-visible &,
    button:hover &,
    a:hover & {
      ${hoverStyles};
    }

    button:active &,
    a:active & {
      ${activeStyles};
    }
  }

  &:not(span) {
    cursor: pointer;

    &.focus-ring {
      outline: auto;
      outline-offset: 5px;
      outline-width: 2px;
      outline-color: var(--borderHoverColour, var(--bgColour));
    }

    &.focus-ring,
    &:hover {
      ${hoverStyles};
    }

    &:active {
      ${activeStyles};
    }
  }

  /* Increase specificity to override :not(span) style */
  &:disabled:disabled:disabled {
    opacity: 0.66;
    background-size: 0% 100%;
    pointer-events: none;
  }
`;
export const DefaultTextOnly = css `
  ${ButtonReset};
  ${transition('background-color, border-color, color', 0.2)};

  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.black};

  display: inline-flex;
  gap: 12px;
  align-items: center;
  color: var(--fgColour);
  text-decoration: underline;
  user-select: none;
  width: auto;

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('background-size, border-color, color, transform', 0.15)};

    &:active {
      transform: translateY(1.5px);
    }
  }

  span& {
    :focus-visible & {
      outline: auto;
      outline-offset: 5px;
      outline-width: 2px;
      outline-color: var(--borderHoverColour, var(--bgColour));
    }

    button:focus-visible &,
    a:focus-visible &,
    button:hover &,
    a:hover & {
      ${hoverStyles};
    }

    button:active &,
    a:active & {
      ${activeStyles};
    }
  }

  &:not(span) {
    cursor: pointer;

    &.focus-ring {
      outline: auto;
      outline-offset: 5px;
      outline-width: 2px;
      outline-color: var(--borderHoverColour, var(--bgColour));
    }

    &.focus-ring,
    &:hover {
      ${hoverStyles};
    }

    &:active {
      ${activeStyles};
    }
  }

  /* Increase specificity to override :not(span) style */
  &:disabled:disabled:disabled {
    opacity: 0.66;
    background-size: 0% 100%;
    pointer-events: none;
  }
`;
export const WhiteButtonStyles = css `
  --bgColour: ${brand.global.white};
  --bgHoverColour: ${colourWithTransparency(brand.global.white, 0.8)};
  --bgActiveColour: ${colourWithTransparency(brand.global.white, 0.7)};
  --borderColour: transparent;
  --borderHoverColour: transparent;
  --borderActiveColour: transparent;
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.black};
`;
export const OutlineButtonStyles = css `
  --bgColour: transparent;
  --bgHoverColour: ${brand.global.white};
  --bgActiveColour: ${colourWithTransparency(brand.global.white, 0.6)};
  --borderColour: ${brand.global.black};
  --borderHoverColour: ${brand.global.black};
  --borderActiveColour: ${brand.global.black};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.black};
`;
export const PearButtonStyles = css `
  --bgColour: ${brand.primary.pear.pear100};
  --bgHoverColour: ${brand.primary.pear.pear50};
  --bgActiveColour: ${brand.primary.pear.pear75};
  --borderColour: ${brand.primary.pear.pear100};
  --borderHoverColour: ${brand.primary.pear.pear50};
  --borderActiveColour: ${brand.primary.pear.pear100};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.black};
`;
export const BlueButtonStyles = css `
  --bgColour: ${brand.primary.blue.blue100};
  --bgHoverColour: ${brand.primary.blue.blue50};
  --bgActiveColour: ${brand.primary.blue.blue100};
  --borderColour: ${brand.primary.blue.blue100};
  --borderHoverColour: ${brand.primary.blue.blue50};
  --borderActiveColour: ${brand.primary.blue.blue50};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.black};
`;
export const BlackButtonStyles = css `
  --bgColour: ${brand.global.black};
  --bgHoverColour: ${brand.accent.darkBlue.darkBlue100};
  --bgActiveColour: ${brand.global.black};
  --borderColour: ${brand.global.black};
  --borderHoverColour: ${brand.accent.darkBlue.darkBlue100};
  --borderActiveColour: ${brand.accent.darkBlue.darkBlue100};
  --fgColour: ${brand.global.white};
  --fgHoverColour: ${brand.global.white};
  --fgActiveColour: ${brand.global.white};
`;
export const DarkBlueButtonStyles = css `
  --bgColour: ${brand.accent.darkBlue.darkBlue100};
  --bgHoverColour: ${brand.accent.darkBlue.darkBlue50};
  --bgActiveColour: ${brand.accent.darkBlue.darkBlue100};
  --borderColour: ${brand.accent.darkBlue.darkBlue100};
  --borderHoverColour: ${brand.accent.darkBlue.darkBlue50};
  --borderActiveColour: ${brand.accent.darkBlue.darkBlue50};
  --fgColour: ${brand.global.white};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.white};
`;
export const ForrestGreenButtonStyles = css `
  --bgColour: ${brand.accent.forrestGreen.forrestGreen100};
  --bgHoverColour: ${brand.accent.forrestGreen.forrestGreen50};
  --bgActiveColour: ${brand.accent.forrestGreen.forrestGreen100};
  --borderColour: ${brand.accent.forrestGreen.forrestGreen100};
  --borderHoverColour: ${brand.accent.forrestGreen.forrestGreen50};
  --borderActiveColour: ${brand.accent.forrestGreen.forrestGreen50};
  --fgColour: ${brand.global.white};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.white};
`;
export const PurpleButtonStyles = css `
  --bgColour: ${brand.secondary.purple.purple100};
  --bgHoverColour: ${brand.secondary.purple.purple50};
  --bgActiveColour: ${brand.secondary.purple.purple75};
  --borderColour: ${brand.secondary.purple.purple100};
  --borderHoverColour: ${brand.secondary.purple.purple50};
  --borderActiveColour: ${brand.secondary.purple.purple100};
  --fgColour: ${brand.global.white};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.white};
`;
export const OrangeButtonStyles = css `
  --bgColour: ${brand.secondary.orange.orange100};
  --bgHoverColour: ${brand.secondary.orange.orange50};
  --bgActiveColour: ${brand.secondary.orange.orange100};
  --borderColour: ${brand.secondary.orange.orange100};
  --borderHoverColour: ${brand.secondary.orange.orange50};
  --borderActiveColour: ${brand.secondary.orange.orange50};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.black};
`;
export const YellowButtonStyles = css `
  --bgColour: ${brand.secondary.yellow.yellow100};
  --bgHoverColour: ${brand.secondary.yellow.yellow50};
  --bgActiveColour: ${brand.secondary.yellow.yellow100};
  --borderColour: ${brand.secondary.yellow.yellow100};
  --borderHoverColour: ${brand.secondary.yellow.yellow50};
  --borderActiveColour: ${brand.secondary.yellow.yellow50};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.black};
`;
export const EngageGreenButtonStyles = css `
  --bgColour: ${brand.engagePrimary.green.green100};
  --bgHoverColour: ${brand.engagePrimary.green.green50};
  --bgActiveColour: ${brand.engagePrimary.green.green100};
  --borderColour: ${brand.engagePrimary.green.green100};
  --borderHoverColour: ${brand.engagePrimary.green.green50};
  --borderActiveColour: ${brand.engagePrimary.green.green50};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.white};
`;
export const EngageSeaGreenButtonStyles = css `
  --bgColour: ${brand.engagePrimary.seaGreen.seaGreen100};
  --bgHoverColour: ${brand.engagePrimary.seaGreen.seaGreen50};
  --bgActiveColour: ${brand.engagePrimary.seaGreen.seaGreen100};
  --borderColour: ${brand.engagePrimary.seaGreen.seaGreen100};
  --borderHoverColour: ${brand.engagePrimary.seaGreen.seaGreen50};
  --borderActiveColour: ${brand.engagePrimary.seaGreen.seaGreen50};
  --fgColour: ${brand.global.white};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.white};
`;
export const EngageTealButtonStyles = css `
  --bgColour: ${brand.engagePrimary.teal.teal100};
  --bgHoverColour: ${brand.engagePrimary.teal.teal50};
  --bgActiveColour: ${brand.engagePrimary.teal.teal100};
  --borderColour: ${brand.engagePrimary.teal.teal100};
  --borderHoverColour: ${brand.engagePrimary.teal.teal50};
  --borderActiveColour: ${brand.engagePrimary.teal.teal50};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.white};
`;
export const EngageLimeButtonStyles = css `
  --bgColour: ${brand.engagePrimary.lime.lime100};
  --bgHoverColour: ${brand.engagePrimary.lime.lime50};
  --bgActiveColour: ${brand.engagePrimary.lime.lime100};
  --borderColour: ${brand.engagePrimary.lime.lime100};
  --borderHoverColour: ${brand.engagePrimary.lime.lime50};
  --borderActiveColour: ${brand.engagePrimary.lime.lime50};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.black};
`;
export const EngageSkyButtonStyles = css `
  --bgColour: ${brand.engagePrimary.sky.sky100};
  --bgHoverColour: ${brand.engagePrimary.sky.sky50};
  --bgActiveColour: ${brand.engagePrimary.sky.sky100};
  --borderColour: ${brand.engagePrimary.sky.sky100};
  --borderHoverColour: ${brand.engagePrimary.sky.sky50};
  --borderActiveColour: ${brand.engagePrimary.sky.sky50};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.black};
`;
export const EngagePurpleButtonStyles = css `
  --bgColour: ${brand.engageSecondary.purple.purple100};
  --bgHoverColour: ${brand.engageSecondary.purple.purple50};
  --bgActiveColour: ${brand.engageSecondary.purple.purple100};
  --borderColour: ${brand.engageSecondary.purple.purple100};
  --borderHoverColour: ${brand.engageSecondary.purple.purple50};
  --borderActiveColour: ${brand.engageSecondary.purple.purple50};
  --fgColour: ${brand.global.white};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.white};
`;
export const EngageLilacButtonStyles = css `
  --bgColour: ${brand.engageSecondary.lilac.lilac100};
  --bgHoverColour: ${brand.engageSecondary.lilac.lilac50};
  --bgActiveColour: ${brand.engageSecondary.lilac.lilac100};
  --borderColour: ${brand.engageSecondary.lilac.lilac100};
  --borderHoverColour: ${brand.engageSecondary.lilac.lilac50};
  --borderActiveColour: ${brand.engageSecondary.lilac.lilac50};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.white};
`;
export const EngageTangerineButtonStyles = css `
  --bgColour: ${brand.engageSecondary.tangerine.tangerine100};
  --bgHoverColour: ${brand.engageSecondary.tangerine.tangerine50};
  --bgActiveColour: ${brand.engageSecondary.tangerine.tangerine100};
  --borderColour: ${brand.engageSecondary.tangerine.tangerine100};
  --borderHoverColour: ${brand.engageSecondary.tangerine.tangerine50};
  --borderActiveColour: ${brand.engageSecondary.tangerine.tangerine50};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.white};
`;
export const EngageLavenderButtonStyles = css `
  --bgColour: ${brand.engageSecondary.lavender.lavender100};
  --bgHoverColour: ${brand.engageSecondary.lavender.lavender50};
  --bgActiveColour: ${brand.engageSecondary.lavender.lavender100};
  --borderColour: ${brand.engageSecondary.lavender.lavender100};
  --borderHoverColour: ${brand.engageSecondary.lavender.lavender50};
  --borderActiveColour: ${brand.engageSecondary.lavender.lavender50};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.white};
`;
export const EngagePinkButtonStyles = css `
  --bgColour: ${brand.engageSecondary.pink.pink100};
  --bgHoverColour: ${brand.engageSecondary.pink.pink50};
  --bgActiveColour: ${brand.engageSecondary.pink.pink100};
  --borderColour: ${brand.engageSecondary.pink.pink100};
  --borderHoverColour: ${brand.engageSecondary.pink.pink50};
  --borderActiveColour: ${brand.engageSecondary.pink.pink50};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.black};
`;
export const EngageYellowButtonStyles = css `
  --bgColour: ${brand.engageSecondary.yellow.yellow100};
  --bgHoverColour: ${brand.engageSecondary.yellow.yellow50};
  --bgActiveColour: ${brand.engageSecondary.yellow.yellow100};
  --borderColour: ${brand.engageSecondary.yellow.yellow100};
  --borderHoverColour: ${brand.engageSecondary.yellow.yellow50};
  --borderActiveColour: ${brand.engageSecondary.yellow.yellow50};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.black};
`;
export const BronzeButtonStyles = css `
  --bgColour: ${brand.awards.bronze.bronze100};
  --bgHoverColour: ${brand.awards.bronze.bronze50};
  --bgActiveColour: ${brand.awards.bronze.bronze100};
  --borderColour: ${brand.awards.bronze.bronze100};
  --borderHoverColour: ${brand.awards.bronze.bronze50};
  --borderActiveColour: ${brand.awards.bronze.bronze50};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.black};
`;
export const SilverButtonStyles = css `
  --bgColour: ${brand.awards.silver.silver100};
  --bgHoverColour: ${brand.awards.silver.silver50};
  --bgActiveColour: ${brand.awards.silver.silver100};
  --borderColour: ${brand.awards.silver.silver100};
  --borderHoverColour: ${brand.awards.silver.silver50};
  --borderActiveColour: ${brand.awards.silver.silver50};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.black};
`;
export const GoldButtonStyles = css `
  --bgColour: ${brand.awards.gold.gold100};
  --bgHoverColour: ${brand.awards.gold.gold50};
  --bgActiveColour: ${brand.awards.gold.gold100};
  --borderColour: ${brand.awards.gold.gold100};
  --borderHoverColour: ${brand.awards.gold.gold50};
  --borderActiveColour: ${brand.awards.gold.gold50};
  --fgColour: ${brand.global.black};
  --fgHoverColour: ${brand.global.black};
  --fgActiveColour: ${brand.global.black};
`;
const Container = styled.button `
  &[data-text-only='false'] {
    ${DefaultButtonStyles};
  }

  &[data-text-only='true'] {
    ${DefaultTextOnly};
  }

  &[data-type='pear'] {
    ${PearButtonStyles};
  }

  &[data-type='blue'] {
    ${BlueButtonStyles};
  }

  &[data-type='black'] {
    ${BlackButtonStyles};
  }

  &[data-type='dark-blue'] {
    ${DarkBlueButtonStyles};
  }

  &[data-type='forrest-green'] {
    ${ForrestGreenButtonStyles};
  }

  &[data-type='purple'] {
    ${PurpleButtonStyles};
  }

  &[data-type='orange'] {
    ${OrangeButtonStyles};
  }

  &[data-type='yellow'] {
    ${YellowButtonStyles};
  }

  &[data-type='engage-green'] {
    ${EngageGreenButtonStyles};
  }

  &[data-type='engage-sea-green'] {
    ${EngageSeaGreenButtonStyles};
  }

  &[data-type='engage-teal'] {
    ${EngageTealButtonStyles};
  }

  &[data-type='engage-lime'] {
    ${EngageLimeButtonStyles};
  }

  &[data-type='engage-sky'] {
    ${EngageSkyButtonStyles};
  }

  &[data-type='engage-purple'] {
    ${EngagePurpleButtonStyles};
  }

  &[data-type='engage-lilac'] {
    ${EngageLilacButtonStyles};
  }

  &[data-type='engage-tangerine'] {
    ${EngageTangerineButtonStyles};
  }

  &[data-type='engage-lavender'] {
    ${EngageLavenderButtonStyles};
  }

  &[data-type='engage-pink'] {
    ${EngagePinkButtonStyles};
  }

  &[data-type='engage-yellow'] {
    ${EngageYellowButtonStyles};
  }

  &[data-type='bronze'] {
    ${BronzeButtonStyles};
  }

  &[data-type='silver'] {
    ${SilverButtonStyles};
  }

  &[data-type='gold'] {
    ${GoldButtonStyles};
  }

  &[data-type='white'] {
    ${WhiteButtonStyles};
  }

  &[data-type='outline'] {
    ${OutlineButtonStyles};
  }

  &[data-icon-only='true'] {
    height: var(--buttonHeight);
    justify-content: center;
    padding: 0;
    width: var(--buttonHeight);
  }
`;
export const CommonButtonTextStyles = css `
  ${transition('color', 0.2)};
  ${fonts.gotham.bold};

  [data-type='engage'] & {
    ${fonts.plusJakarta.bold};
  }

  color: currentColor;
  text-decoration: none;
  font-size: ${fluid(14, 16)};
  line-height: ${fluid(20, 22)};
`;
const Text = styled.span `
  ${CommonButtonTextStyles};

  [data-icon-only='true'] & {
    ${srOnly};
  }
`;
export const ButtonIconWrapperStyles = css `
  --padding: 3px;
  --iconHeight: 20px;

  display: grid;
  place-content: center;
  color: var(--iconTextColour, var(--fgColour));
  background-color: var(--iconBackgroundColour, var(--bgColour));
  margin: 0;
  height: calc(var(--iconHeight));
  width: calc(var(--iconHeight));
  border-radius: 5000px;

  > span,
  > svg {
    ${transition('transform')};
    display: block;
    height: var(--iconHeight);
    width: var(--iconHeight);
  }
`;
const IconWrapper = styled.div `
  ${transition('color, background-color', 0.2)};
  ${ButtonIconWrapperStyles};

  [data-icon-position='left'] & {
    order: -1;
  }

  [data-icon-only='true'] & {
    --iconHeight: 24px;

    height: calc(var(--buttonHeight) - 2px);
    width: var(--buttonHeight);
  }

  [data-icon-type=${SystemIcon.Loader}] & {
    --iconHeight: 16px
    --padding: 0;

    background-color: var(--iconTextColour, var(--fgColour));
    color: var(--iconBackgroundColour, var(--bgColour));
  }

  [data-icon-type=${SystemIcon.ArrowDown}] & {
    transform: rotate(90deg);
  }

  [data-icon-type=${SystemIcon.ArrowDown}] &,
  [data-icon-type=${SystemIcon.ArrowRight}] & {
    ${transition('transform, color, background-color', 0.2)};
  }
`;
const ButtonStyles = {
    Container,
    IconWrapper,
    Text,
};
export default ButtonStyles;
